import { Card, Spinner } from '@ohear/ui'
import { Navigate } from '@tanstack/react-router'
/**
 * AdminGuard — gates the entire /admin/* surface.
 *
 * Reads the platform-staff probe from the API. While the probe is in flight
 * we render a small spinner (no flash of admin UI). On a definitive "not
 * admin" answer we redirect to `/`. On 5xx we surface a small error so the
 * user can retry — better than silently hiding the surface.
 *
 * The server-side `requirePlatformAdmin` middleware is the actual security
 * boundary; this component is purely UX gating.
 */
import type { ReactNode } from 'react'
import { useAdminWhoami } from '../../lib/api/admin'
import { useAuth } from '../../lib/auth'

export interface AdminGuardProps {
  children: ReactNode
}

export function AdminGuard({ children }: AdminGuardProps) {
  const { session, loading: sessionLoading } = useAuth()
  const probe = useAdminWhoami()

  // Not signed in at all → bounce to login. (Login page redirects to /
  // post-auth; admin URL is not preserved on purpose.)
  if (!sessionLoading && !session) {
    return <Navigate to="/login" replace />
  }

  if (sessionLoading || probe.isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 text-[var(--text-2)] p-6"
      >
        <Spinner /> Verifying access...
      </div>
    )
  }

  if (probe.isError) {
    return (
      <Card>
        <p className="m-0 text-[13px]">
          Could not verify admin access. Please try again in a moment.
        </p>
      </Card>
    )
  }

  if (!probe.data?.is_admin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
